import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper';
import 'swiper/dist/css/swiper.min.css';
import { bigInfoContent } from './BigInfo.content';
import cloud1 from './assets/cloud-1.webp';
import cloud2 from './assets/cloud-2.webp';
import styles from './BigInfo.module.sass';

gsap.registerPlugin(ScrollTrigger);

const TRANSITION_DURATION = 0.55;
const TRANSITION_EASE = 'power2.inOut';

function applySettledState(slideIndex, n, bgRefs, innerRefs) {
  for (let i = 0; i < n; i++) {
    const bg = bgRefs.current[i];
    const inner = innerRefs.current[i];
    const state = {
      opacity: i === slideIndex ? 1 : 0,
      xPercent: 0,
      clearProps: 'zIndex',
    };
    if (bg) gsap.set(bg, state);
    if (inner) gsap.set(inner, state);
  }
}

function playTransition(to, n, bgRefs, innerRefs, onComplete) {
  const tl = gsap.timeline({ onComplete });

  const dur = TRANSITION_DURATION;
  const ease = TRANSITION_EASE;

  for (let i = 0; i < n; i++) {
    const bg = bgRefs.current[i];
    const inner = innerRefs.current[i];
    const isTarget = i === to;

    if (bg) {
      gsap.set(bg, { zIndex: isTarget ? 2 : 1 });
      tl.to(
        bg,
        {
          opacity: isTarget ? 1 : 0,
          xPercent: 0,
          duration: dur,
          ease,
          overwrite: true,
        },
        0
      );
    }
    if (inner) {
      gsap.set(inner, { zIndex: isTarget ? 2 : 1 });
      tl.to(
        inner,
        {
          opacity: isTarget ? 1 : 0,
          xPercent: 0,
          duration: dur,
          ease,
          overwrite: true,
        },
        0
      );
    }
  }

  return tl;
}

function renderHeading(heading, s) {
  const { lines } = heading;
  return (
    <h2 className={s.title}>
      {lines.map((line, lineIndex) => (
        <span key={lineIndex} className={s.titleLine}>
          {line.map((seg, segIndex) => {
            const segmentClass = [
              s.titleSegment,
              seg.accent ? s.titleSegmentAccent : s.titleSegmentMuted,
              seg.caps ? s.titleSegmentCaps : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <span key={segIndex} className={segmentClass}>
                {seg.text}
              </span>
            );
          })}
        </span>
      ))}
    </h2>
  );
}

function slideStyles(styles) {
  return {
    title: styles.bigInfo__title,
    titleLine: styles.bigInfo__titleLine,
    titleSegment: styles.bigInfo__titleSegment,
    titleSegmentAccent: styles.bigInfo__titleSegmentAccent,
    titleSegmentMuted: styles.bigInfo__titleSegmentMuted,
    titleSegmentCaps: styles.bigInfo__titleSegmentCaps,
    body: styles.bigInfo__body,
    text: styles.bigInfo__text,
    textAccent: styles.bigInfo__textAccent,
    textMuted: styles.bigInfo__textMuted,
  };
}

function renderSlideContent(slide, styles) {
  const { body } = slide;
  const s = slideStyles(styles);
  return (
    <>
      {renderHeading(slide.heading, s)}
      <div className={s.body}>
        {body.map((block, i) => (
          <p key={i} className={s.text}>
            {block.segments.map((seg, j) => (
              <span key={j} className={seg.accent ? s.textAccent : s.textMuted}>
                {seg.text}
              </span>
            ))}
          </p>
        ))}
      </div>
    </>
  );
}

function renderClouds(s) {
  return (
    <div className={s.bigInfo__clouds} aria-hidden>
      <img src={cloud1} alt="" className={s.bigInfo__cloud1} decoding="async" />
      <img src={cloud2} alt="" className={s.bigInfo__cloud2} decoding="async" />
    </div>
  );
}

export function BigInfo() {
  const sectionRef = useRef(null);
  const stickyRef = useRef(null);
  const swiperRef = useRef(null);
  const bgRefs = useRef([]);
  const innerRefs = useRef([]);
  const { slides } = bigInfoContent;
  const n = slides.length;

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(max-width: 900px)').matches;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.matchMedia('(max-width: 900px)').matches);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    if (!swiperRef.current) return;

    let swiper = null;
    const timer = setTimeout(() => {
      if (swiperRef.current) {
        swiper = new Swiper(swiperRef.current, {
          effect: 'fade',
          fadeEffect: { crossFade: true },
          loop: true,
          autoplay: {
            delay: 4000,
            disableOnInteraction: false,
          },
          speed: 600,
        });
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (swiper) swiper.destroy();
    };
  }, [isMobile]);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const sticky = stickyRef.current;
    if (!section || !sticky) return;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced || n < 2 || isMobile) {
      return;
    }

    const hasRefs = slides.every((_, i) => bgRefs.current[i] && innerRefs.current[i]);
    if (!hasRefs) return;

    const bgRefsArray = bgRefs.current;
    const innerRefsArray = innerRefs.current;

    const currentSlideRef = { current: 0 };
    let transitionTl = null;

    applySettledState(0, n, bgRefs, innerRefs);

    const getScrollDistance = () => Math.max(1, (n - 1) * sticky.offsetHeight);

    const st = ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: () => `+=${getScrollDistance()}`,
      pin: !isMobile,
      pinSpacing: !isMobile,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      refreshPriority: -20,
      onUpdate: (self) => {
        const scrollTarget = Math.min(Math.floor(self.progress * n), n - 1);

        if (scrollTarget === currentSlideRef.current) {
          return;
        }

        currentSlideRef.current = scrollTarget;

        if (transitionTl) {
          transitionTl.kill();
        }

        transitionTl = playTransition(scrollTarget, n, bgRefs, innerRefs, () => {
          transitionTl = null;
        });
      },
    });

    const refresh = () => {
      ScrollTrigger.sort();
      ScrollTrigger.refresh();
    };
    refresh();
    const rafId = requestAnimationFrame(refresh);
    window.addEventListener('load', refresh);
    window.addEventListener('resize', refresh);

    return () => {
      window.removeEventListener('load', refresh);
      window.removeEventListener('resize', refresh);
      cancelAnimationFrame(rafId);
      if (transitionTl) {
        transitionTl.kill();
      }
      const all = [];
      for (let i = 0; i < n; i++) {
        if (bgRefsArray[i]) all.push(bgRefsArray[i]);
        if (innerRefsArray[i]) all.push(innerRefsArray[i]);
      }
      gsap.killTweensOf(all);
      st.kill();
    };
  }, [n, slides, isMobile]);

  const reduced =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (isMobile && !reduced && n >= 2) {
    return (
      <section className={styles.bigInfo}>
        <div className={`swiper-container ${styles.bigInfo__swiper}`} ref={swiperRef}>
          <div className="swiper-wrapper">
            {slides.map((slide, index) => (
              <div key={index} className="swiper-slide">
                <img src={slide.image} alt="" className={styles.bigInfo__image} decoding="async" />
                <div className={styles.bigInfo__vignette} aria-hidden />
                <div className={styles.bigInfo__inner}>{renderSlideContent(slide, styles)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (reduced || n < 2) {
    const first = slides[0];
    return (
      <section className={styles.bigInfo}>
        <div ref={stickyRef} className={styles.bigInfo__sticky}>
          <div className={styles.bigInfo__slides}>
            <div className={styles.bigInfo__slide}>
              <img src={first.image} alt="" className={styles.bigInfo__image} decoding="async" />
              <div className={styles.bigInfo__vignette} aria-hidden />
            </div>
          </div>
          {renderClouds(styles)}
          <div className={styles.bigInfo__inners}>
            <div className={styles.bigInfo__innerWrap}>
              <div className={styles.bigInfo__inner}>{renderSlideContent(first, styles)}</div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className={styles.bigInfo}>
      <div ref={stickyRef} className={styles.bigInfo__sticky}>
        <div className={styles.bigInfo__slides}>
          {slides.map((slide, index) => (
            <div
              key={index}
              ref={(el) => {
                bgRefs.current[index] = el;
              }}
              className={styles.bigInfo__slide}
            >
              <img src={slide.image} alt="" className={styles.bigInfo__image} decoding="async" />
              <div className={styles.bigInfo__vignette} aria-hidden />
            </div>
          ))}
        </div>
        {renderClouds(styles)}
        <div className={styles.bigInfo__inners}>
          {slides.map((slide, index) => (
            <div
              key={index}
              ref={(el) => {
                innerRefs.current[index] = el;
              }}
              className={styles.bigInfo__innerWrap}
            >
              <div className={styles.bigInfo__inner}>{renderSlideContent(slide, styles)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
